/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import {omit, cap1LowerRest} from 'mad-utils/lib/react';

/**************************************** PROJECT MODULES *****************************************/
import {inject} from 'mobx-react';
import {polyglotSymbol} from '../../setup/polyglot-factory';

/**************************************** TYPE DEFINITIONS ****************************************/
type InterpolationInjectionProps =
    | {smart_count?: number}
    | {'%s': string}
    | {
          '%1$s': string;
          '%2$s'?: string;
          '%3$s'?: string;
          '%4$s'?: string;
          '%5$s'?: string;
          '%6$s'?: string;
          '%7$s'?: string;
          '%8$s'?: string;
          '%9$s'?: string;
      };

type InjectionsType =
    | (InterpolationInjectionProps)
    | Record<string, string>
    | ((InterpolationInjectionProps) & Record<string, string>);

interface TranslateProps {
    caps?: 'lowercase' | 'allCaps' | 'capFirst' | 'cap1LowerRest';
    capitalize?: boolean;
    injections?: InjectionsType;
    prefix?: string;
}

/**
 * Perform actual translation
 * TODO move into translate function below
 */
const translateStr = (
    str: string,
    {caps = ``, capitalize = false, prefix, injections, polyglot}: TranslateProps & PolyglotProps
) => {
    let outputStr = polyglot.t(
        str && (prefix ? prefix + str : str.sID),
        injections || undefined
    ) as string;

    // Handle special `%s` key case
    if (injections && injections[`%s`]) {
        outputStr = outputStr.replace(`%s`, injections[`%s`]);
    }

    // Handle special interpolation key cases: `%s`, `%1$s`, `%2$s`, ..., `%9$s`
    if (injections && injections[`%s`]) outputStr = outputStr.replace(`%s`, injections[`%s`]);
    if (injections && injections[`%1$s`]) outputStr = outputStr.replace(`%1$s`, injections[`%1$s`]);
    if (injections && injections[`%2$s`]) outputStr = outputStr.replace(`%2$s`, injections[`%2$s`]);
    if (injections && injections[`%3$s`]) outputStr = outputStr.replace(`%3$s`, injections[`%3$s`]);
    if (injections && injections[`%4$s`]) outputStr = outputStr.replace(`%4$s`, injections[`%4$s`]);
    if (injections && injections[`%5$s`]) outputStr = outputStr.replace(`%5$s`, injections[`%5$s`]);
    if (injections && injections[`%6$s`]) outputStr = outputStr.replace(`%6$s`, injections[`%6$s`]);
    if (injections && injections[`%7$s`]) outputStr = outputStr.replace(`%7$s`, injections[`%7$s`]);
    if (injections && injections[`%8$s`]) outputStr = outputStr.replace(`%8$s`, injections[`%8$s`]);
    if (injections && injections[`%9$s`]) outputStr = outputStr.replace(`%9$s`, injections[`%9$s`]);

    if (capitalize && !caps) caps = `capFirst`;

    // prettier-ignore
    switch (caps) {
        case `lowercase`:     return outputStr.toLowerCase();
        case `allCaps`:       return outputStr.toUpperCase();
        case `capFirst`:      return outputStr.capitalize;
        case `cap1LowerRest`: return outputStr.cap1LowerRest();
        default:              return outputStr;
    }
};

/********************************************* EXPORT *********************************************/
/**
 * Convert given [stringID] (string) to translated string with [injections] {object} provided
 */
export const translate = (stringID: string, injections?: InjectionsType, prefix?: string) => {
    const polyglot = window[polyglotSymbol] as PolyglotProps['polyglot'];
    return translateStr(stringID, {polyglot, prefix, ...(injections ? {injections} : {})});
};
